import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { matchPath, Route, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { TwoColumnLayout, Banner, SubmitStates } from '@figure/frontlib-gui';
import {
  useIncentive,
  PRODUCTS,
  useTelemetry,
  INCENTIVE_STATUS,
  paramsToObj,
} from '@figure/frontlib-growth';
import { getTheme, isLocalhost } from 'shared/utils';
import * as Theme from 'shared/constants/theme';
import * as ScreenWidths from 'shared/constants/screen-widths';
import FigureLogo from 'shared/assets/images/figure/figure.svg';
import { useLeaveSurveyModal, useProfile, useRedirect } from 'shared/hooks';
import { ReferralModal } from 'components/Modal';
import Timer from 'shared/assets/images/timer.svg';
import ArrowRight from 'shared/assets/images/arrow-right-purple.svg';
import {
  DashboardFooter,
  Header,
  COLORS,
  SPACINGS,
  cssByBreakpoint,
  BREAKPOINTS,
} from '@figure/figure-frontlib-gui';
import { SidebarContainer } from '../Sidebar';
import FeedbackContainer from '../Feedback/FeedbackContainer';
import { LeaveSurveyContainer } from '../LeaveSurvey';
import MainFooter from '../Footer';

const Page = styled.div`
  background-color: ${COLORS.BACKGROUND_1};
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  header,
  footer {
    width: 100%;
  }

  footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 ${SPACINGS.M}px ${SPACINGS.L}px;
    margin-top: auto;

    & > div {
      max-width: 860px;
      flex: 1 1 auto;
    }
  }

  main {
    ${cssByBreakpoint(
      [
        css`
          padding: 0 ${SPACINGS.M}px;
        `,
        css`
          padding: 0;
        `,
      ],
      [BREAKPOINTS.SM, BREAKPOINTS.MD]
    )}
  }
`;

const MenuItem = styled.span`
  cursor: pointer;
  display: block;
  padding: 10px 15px;
`;

const PageRoute = ({
  isAuthenticated,
  onLogout,
  fullWidthContent,
  contentMaxWidth,
  hasLeaveSurvey,
  isCompleteApplication,
  children,
  isTwoColLayout,
  ...rest
}) => {
  const { t } = useTranslation();
  const theme = getTheme();
  const location = useLocation();
  const { profile } = useProfile(false);
  const [applicationUuid, setApplicationUuid] = useState('');
  const { surveyTriggered, surveyShowDisabled, openSurvey, resetSurvey } = useLeaveSurveyModal();
  const handleLink = (action) => () =>
    hasLeaveSurvey && !surveyTriggered && !surveyShowDisabled ? openSurvey(action) : action();
  const { isAuthenticated: isTelemetryAuthenticated } = useTelemetry();
  const { redirectToDashboard, redirectToLocation } = useRedirect();
  const {
    incentiveGetStatus,
    incentiveSetOpen,
    incentiveVerify,
    incentiveCacheReferrerEmail,
    message,
    status,
    referrerEmail,
  } = useIncentive(PRODUCTS.PERSONAL_LOAN);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitState, setSubmitState] = useState(SubmitStates.DEFAULT);
  const [serverErrors, setServerErrors] = useState({});
  const [bannerMessage, setBannerMessage] = useState('');
  const [incentiveUuid, setIncentiveUuid] = useState('');
  const [incentiveCode, setIncentiveCode] = useState('');
  const renderModal =
    (status !== INCENTIVE_STATUS.OPEN && status !== INCENTIVE_STATUS.PREOPEN) ||
    (!isTelemetryAuthenticated && status === '');

  // Set banner message and form errors
  useEffect(() => {
    const match = matchPath(location.pathname, {
      path: '/applications/:uuid/*',
    });
    if (match) {
      setApplicationUuid(match.params.uuid);
    }
  }, [location, setApplicationUuid]);
  useEffect(() => {
    if (
      status === INCENTIVE_STATUS.INVALID_IDENTITY ||
      status === INCENTIVE_STATUS.INVALID_FULFILLMENT
    ) {
      setBannerMessage('Oops, there was an error with your referral. Click here to try again.');
      setServerErrors({ email: 'Hm, we can’t find a member with that email.' });
      setSubmitState(SubmitStates.DEFAULT);
    } else if (status === INCENTIVE_STATUS.UNKNOWN_ERROR) {
      setBannerMessage('Something unexpected happened. Please try again.');
      setServerErrors({ email: 'Something unexpected happened. Please try again.' });
      setSubmitState(SubmitStates.DEFAULT);
    } else if (status === INCENTIVE_STATUS.OPEN || status === INCENTIVE_STATUS.PREOPEN) {
      setBannerMessage(message);
      setSubmitState(SubmitStates.SUCCESS);
    } else {
      setBannerMessage('Have you been referred by a Figure member?');
    }
  }, [status, setSubmitState, message, setBannerMessage]);
  useEffect(() => {
    if (referrerEmail.toLowerCase() === profile.email.toLowerCase() && profile.email !== '') {
      setBannerMessage('Oops, there was an error with your referral. Click here to try again.');
      setServerErrors({ email: 'Self referral is not allowed.' });
      setSubmitState(SubmitStates.DEFAULT);
    }
  }, [profile.email, referrerEmail]);

  // Deep link flow with incentive uuid and code set in query param.
  useEffect(() => {
    if (location?.search) {
      const params = paramsToObj(location.search);
      const { incentiveCode, incentiveUuid } = params;
      if (incentiveCode) setIncentiveCode(incentiveCode);
      if (incentiveUuid) setIncentiveUuid(incentiveUuid);
    }
  }, [location.search]);

  // Set open when authenticated after register page
  useEffect(() => {
    if (isTelemetryAuthenticated && applicationUuid) {
      // Incentive was verified or referrerEmail/incentiveCode was provided pre-authentication
      if (
        status === INCENTIVE_STATUS.PREOPEN ||
        ((referrerEmail || incentiveCode) && status === '')
      ) {
        incentiveSetOpen(
          incentiveUuid || process.env.REACT_APP_PL_REFERRAL_INCENTIVE_ID,
          applicationUuid,
          referrerEmail,
          incentiveCode
        ).then((res) => {
          if (res.status === INCENTIVE_STATUS.INVALID_IDENTITY) {
            setBannerMessage(
              'Oops, there was an error with your referral. Click here to try again.'
            );
            setServerErrors({ email: 'Hm, we can’t find a member with that email.' });
            setSubmitState(SubmitStates.DEFAULT);
          } else {
            incentiveGetStatus();
          }
        });
      }
      // Get on fresh load
      else if (status === '') {
        incentiveGetStatus();
      }
    }
  }, [
    incentiveGetStatus,
    applicationUuid,
    setSubmitState,
    isTelemetryAuthenticated,
    incentiveUuid,
    setServerErrors,
    incentiveSetOpen,
    referrerEmail,
    setBannerMessage,
    status,
    incentiveCode,
  ]);

  // Deep link referral flow, verify if not authenticated
  useEffect(() => {
    if (incentiveCode && incentiveUuid && !applicationUuid) {
      incentiveVerify(incentiveUuid, incentiveCode).catch(() => {
        setBannerMessage('Something unexpected happened. Please try again.');
        setServerErrors({ email: 'Something unexpected happened. Please try again.' });
        setSubmitState(SubmitStates.DEFAULT);
      });
    }
  }, [
    setSubmitState,
    incentiveUuid,
    incentiveCode,
    incentiveVerify,
    setServerErrors,
    setBannerMessage,
    applicationUuid,
  ]);

  const onPlReferralSubmit = (data) => {
    setSubmitState(SubmitStates.PENDING);
    if (isTelemetryAuthenticated && applicationUuid) {
      incentiveSetOpen(
        process.env.REACT_APP_PL_REFERRAL_INCENTIVE_ID,
        applicationUuid,
        data.email
      ).then((res) => {
        if (res.status === INCENTIVE_STATUS.INVALID_IDENTITY) {
          setBannerMessage('Oops, there was an error with your referral. Click here to try again.');
          setServerErrors({ email: 'Hm, we can’t find a member with that email.' });
          setSubmitState(SubmitStates.DEFAULT);
        } else {
          incentiveCacheReferrerEmail('');
          incentiveGetStatus();
        }
      });
    } else {
      // Missing incentiveCode / incentiveUuid so cannot be verified, set optimistic message and call setOpen when authenticated
      setSubmitState(SubmitStates.SUCCESS);
      setBannerMessage(
        'Almost there! Complete your application to be eligible for a $150 gift card.'
      );
      incentiveCacheReferrerEmail(data.email);
    }
  };

  return (
    <Route {...rest}>
      {isTwoColLayout ? (
        <TwoColumnLayout
          logoSrc={(() => {
            switch (theme) {
              default:
                return FigureLogo;
            }
          })()}
          logoAlt={`${t('companySingleName')} logo`}
          url={process.env.REACT_APP_DASHBOARD_PATH}
          phoneNumber={t('phone.customerService')}
          cta={isAuthenticated && theme === Theme.FIGURE ? <FeedbackContainer /> : null}
          menuList={[
            <MenuItem
              onClick={() => {
                // don't show exit survey
                resetSurvey();
                redirectToDashboard();
              }}
            >
              Dashboard
            </MenuItem>,
            <MenuItem onClick={handleLink(onLogout)}>Sign out</MenuItem>,
          ]}
          isRegistration={!isAuthenticated}
          linkRoute={isLocalhost() ? '/applications/start' : process.env.REACT_APP_LOGIN_PATH}
          linkText={'Log in'}
          renderSidebar={() => <SidebarContainer />}
          renderMainContent={() => (
            <>
              {children}
              {hasLeaveSurvey && <LeaveSurveyContainer />}
              <MainFooter />
            </>
          )}
          renderBanner={() =>
            !isCompleteApplication && (
              <Banner
                renderMessage={
                  status === INCENTIVE_STATUS.OPEN || status === INCENTIVE_STATUS.PREOPEN ? (
                    <>
                      <img src={Timer} alt="timer" />
                      {bannerMessage}
                    </>
                  ) : (
                    <>
                      {bannerMessage}
                      <img src={ArrowRight} alt="arrow right" />
                    </>
                  )
                }
                renderModal={
                  renderModal ? (
                    <ReferralModal
                      isOpen={isModalOpen}
                      handleClose={() => setIsModalOpen(false)}
                      handleSubmit={onPlReferralSubmit}
                      submitState={submitState}
                      serverErrors={serverErrors}
                      referrerEmail={referrerEmail}
                      refereeEmail={profile.email}
                    />
                  ) : null
                }
                handleClick={() => setIsModalOpen(true)}
              />
            )
          }
          fillContent={fullWidthContent}
          contentMaxWidth={contentMaxWidth}
        />
      ) : (
        <Page>
          <Header
            menuList={
              isAuthenticated
                ? [
                    <MenuItem
                      onClick={() => {
                        // don't show exit survey
                        resetSurvey();
                        redirectToDashboard();
                      }}
                    >
                      Dashboard
                    </MenuItem>,
                    <MenuItem onClick={handleLink(onLogout)}>Sign out</MenuItem>,
                  ]
                : [<MenuItem onClick={() => redirectToLocation('/login')}>Sign in</MenuItem>]
            }
          />
          {children}
          {hasLeaveSurvey && <LeaveSurveyContainer />}
          <DashboardFooter />
        </Page>
      )}
    </Route>
  );
};

PageRoute.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  fullWidthContent: PropTypes.bool,
  contentMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasLeaveSurvey: PropTypes.bool,
  isCompleteApplication: PropTypes.bool.isRequired,
  isTwoColLayout: PropTypes.bool,
};

PageRoute.defaultProps = {
  fullWidthContent: false,
  hasLeaveSurvey: false,
  contentMaxWidth: ScreenWidths.DEFAULT_WIDTH,
  isTwoColLayout: true,
};

export default PageRoute;
